// Small shared presentational components used across pages.

// THE page-level loading state: the blue ring, centered in the main panel
// (everything right of the sidebar), with one short line under it. It is
// DataTable's own ring, so a loading page and a loading table read the same.
// Until 2026-10-13 each page spelled its own — "Reading Stripe…" as a line of
// text under the header, "Reading production…" centered in half the viewport,
// "Loading…" in a bordered box — and the user asked for one thing in one
// place ("not in random places"). A page renders this in place of its
// content, under its header if it keeps one, inside a flex column that
// fills the panel (`flex min-h-full flex-col`, or Activity's frames):
// `flex-1` then takes whatever height the header leaves, and the ring lands
// in the middle of that. Not `min-h-full` here — that made the loader as
// tall as the whole panel even under a header, which pushed the ring below
// the visible middle and gave the page a scrollbar the header's height.
export function PanelLoading({ label = 'Loading…', className = '' }) {
  return (
    <div className={`flex flex-1 flex-col items-center justify-center gap-y-3 p-10 ${className}`}>
      <span
        className="h-7 w-7 animate-spin rounded-full [border:2px_solid_var(--table-line)] [border-top-color:var(--primary)] motion-reduce:animate-none"
        role="status"
        aria-label={label}
      />
      <span className="text-[13px] text-dim">{label}</span>
    </div>
  )
}

export function PageHeader({ title, subtitle, actions }) {
  return (
    // Wraps on a phone: side-by-side, the action button gets squeezed past the
    // right edge instead of shrinking.
    <div className="mb-5 flex items-start justify-between gap-x-4 gap-y-3 max-md:flex-wrap">
      <div className="min-w-0">
        <h1 className="m-0 text-[24px] font-bold tracking-[-0.02em] text-ink max-md:text-[20px]">{title}</h1>
        {subtitle && <p className="m-0 mt-1 text-[14px] text-dim">{subtitle}</p>}
      </div>
      {actions && <div className="flex flex-wrap items-center gap-2">{actions}</div>}
    </div>
  )
}

// One KPI card geometry for the whole app. Every page used to inline its own
// copy of this class string, so resizing the card meant editing 20 places and
// the pages always drifted apart by a few pixels. Change the size here.
//
// The card is taller than its content needs, and the value/label pair sits at
// the top-left corner of the padding box — not centred in the leftover space.
const KPI_SHELL =
  'flex h-[136px] w-[248px] max-w-full flex-none cursor-pointer flex-col items-start justify-start overflow-hidden rounded-[12px] border border-line bg-surface px-[22px] py-[18px] shadow-card'

// The row that holds them. The cards are a fixed width, so how many fit is
// decided by the page's own column: four need 1028px at this gap, five 1288px,
// and a page narrower than that wraps the rest onto the next line. That is the
// trade for a fixed card size. (Activity used to cap itself at 1100px and so
// always wrapped its fifth card; it runs full width now, so on a wide monitor
// five sit in a row.) Pages sitting above a filled table let the table shrink
// when the row wraps, never the row.
export const KPI_ROW = 'flex flex-wrap gap-3'

/**
 * A stat card, read top to bottom: what it is, what it says, and — when there
 * is one — what it means.
 *
 *   row 1  icon + label      quiet, so the eye lands on the number
 *   row 2  the value         the reason the card exists
 *   row 3  note + delta      OPTIONAL. Only render it when the card genuinely
 *                            has a comparison; an empty third row is worse
 *                            than none, and a "+0.00%" nobody computed is worse
 *                            than either.
 *
 * `delta` is { text, dir } where dir is 'up' | 'down' | 'flat'. Direction picks
 * the colour but never the sign — callers pass the text they mean, because
 * whether "up" is good depends on the metric (replies up, bounces up).
 */
// A tile that opens something (the Activity page's "every number opens the
// rows behind it") is a real <button>, not a div with a click handler: it gets
// keyboard focus, Enter/Space, and an aria-pressed state for free, and a
// screen reader announces it as a control. A div wrapped in a button would be
// invalid HTML, so the element itself switches. Tiles without `onClick` render
// exactly as before.
const KPI_ACTIVE = 'border-primary ring-2 ring-primary/30'
const KPI_BUTTON_EXTRA =
  'appearance-none text-left transition-shadow hover:shadow-[0_4px_14px_rgba(15,23,42,0.08)] focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40'

export function KpiShell({
  label,
  children,
  valueClass = 'text-ink',
  labelTitle,
  icon: Icon,
  note,
  delta,
  onClick,
  active = false,
}) {
  const dir = delta?.dir || 'flat'
  const deltaTone =
    dir === 'up' ? 'text-good' : dir === 'down' ? 'text-danger' : 'text-dim'
  const body = (
    <>
      {/* A TOOLTIP NOBODY CAN SEE IS NOT A DISCLOSURE. When a caller hands in
          `labelTitle` the label says so — dotted underline and the help cursor,
          the two cues a reader already knows — because tiles use this to carry
          the caveat behind their number (which window a streak counted, what a
          rate is a share of) and a bare `title` on a plain div announces
          nothing at all. It is still unreachable on a touch screen, so a
          caveat that changes how the number should be READ belongs on the page
          as well; this only makes the extra detail findable. Tiles without a
          title render exactly as before. */}
      <div
        className={`flex items-center gap-x-1.5 text-[13px] leading-[1.35] text-dim ${labelTitle ? 'cursor-help' : ''}`}
        title={labelTitle}
      >
        {Icon && <Icon className="h-[15px] w-[15px] flex-none text-faint" aria-hidden="true" />}
        {/* The underline goes on the SPAN, not the row: text-decoration-style
            does not inherit, so a dotted rule set on the flex parent would
            reach the label as a solid one. */}
        <span className={`min-w-0 truncate ${labelTitle ? 'underline decoration-dotted underline-offset-[3px]' : ''}`}>{label}</span>
      </div>
      <div
        className={`mt-2 text-[26px] font-bold leading-[1.15] tracking-[-0.02em] tabular-nums ${valueClass}`}
      >
        {children}
      </div>
      {(note || delta) && (
        <div className="mt-auto flex w-full items-baseline justify-between gap-x-2 pt-2">
          <span className="min-w-0 truncate text-[12px] text-faint">{note}</span>
          {delta?.text && (
            <span className={`flex-none text-[12px] font-semibold tabular-nums ${deltaTone}`}>
              {delta.text}
            </span>
          )}
        </div>
      )}
    </>
  )
  if (typeof onClick === 'function') {
    return (
      <button
        type="button"
        aria-pressed={active ? 'true' : 'false'}
        onClick={onClick}
        className={`${KPI_SHELL} ${KPI_BUTTON_EXTRA} ${active ? KPI_ACTIVE : ''}`}
      >
        {body}
      </button>
    )
  }
  return <div className={KPI_SHELL}>{body}</div>
}

export function KpiCard({ label, value, icon, note, delta }) {
  return (
    <KpiShell label={label} icon={icon} note={note} delta={delta}>
      {value}
    </KpiShell>
  )
}

